import React, { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useEscaCopy, toEscaLang } from '../i18n/escaCopy';
import { useSEO } from '../hooks/useSEO';
import { initScrollDepthTracking } from '../utils/analytics';
import { escaContacts } from '../data/escaContacts';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { FaqSection } from '../components/FaqSection';
import { ClassicHero } from '../components/escaClassic/ClassicHero';
import { ClassicScenarios } from '../components/escaClassic/ClassicScenarios';
import { ClassicExpertise } from '../components/escaClassic/ClassicExpertise';
import { ClassicResearch } from '../components/escaClassic/ClassicResearch';
import { ClassicProcess } from '../components/escaClassic/ClassicProcess';
import { ClassicShortlist } from '../components/escaClassic/ClassicShortlist';
import { ClassicCases } from '../components/escaClassic/ClassicCases';
import { ClassicFounder } from '../components/escaClassic/ClassicFounder';
import { ClassicProof } from '../components/escaClassic/ClassicProof';
import { ClassicGeography } from '../components/escaClassic/ClassicGeography';
import { ClassicGuaranteePrice } from '../components/escaClassic/ClassicGuaranteePrice';
import { ClassicDiscuss } from '../components/escaClassic/ClassicDiscuss';
const SEO = {
  UA: {
    title: 'ESCA — Executive Search: пошук керівників і рідкісних спеціалістів',
    description: 'Executive Search для C-level, B-level і складних IT-позицій. Досліджуємо ринок і виходимо напряму на кандидатів, які не шукають роботу. 20+ років, 3 500+ закритих вакансій, worldwide.',
    keywords: 'executive search, пошук керівників, хедхантинг, C-level, підбір топ-менеджерів, ESCA'
  },
  EN: {
    title: 'ESCA — Executive Search for leaders and rare specialists',
    description: 'Executive Search for C-level, B-level and hard-to-fill IT roles. We map the market and approach candidates who are not looking for a job. 20+ years, 3,500+ roles filled, worldwide.',
    keywords: 'executive search, headhunting, C-level recruitment, leadership hiring, ESCA'
  }
} as const;
export function EscaClassicPage() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const seo = SEO[toEscaLang(lang)];
  useSEO({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords
  });
  useEffect(() => initScrollDepthTracking(), []);
  useEffect(() => {
    const id = 'esca-classic-jsonld';
    document.getElementById(id)?.remove();
    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify([{
      '@context': 'https://schema.org',
      '@type': 'ProfessionalService',
      name: 'ESCA',
      description: seo.description,
      email: escaContacts.email,
      telephone: escaContacts.phones[0],
      areaServed: c.geography.regions,
      sameAs: [escaContacts.linkedin, escaContacts.telegram],
      founder: {
        '@type': 'Person',
        name: c.founder.name,
        jobTitle: c.founder.role
      }
    }, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: c.faq.items.map(item => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a
        }
      }))
    }]);
    document.head.appendChild(script);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, [c, seo.description]);
  return <div className="min-h-screen w-full bg-white overflow-x-hidden" data-id="element-1529" data-mp="17x4c6t4bcwkl">
      <Header data-id="element-1530" data-mp="17u9s52p7pdrp" />
      <main className="pt-16 lg:pt-20" data-id="element-1531" data-mp="13v98jhywu73n">
        <ClassicHero data-id="element-1532" data-mp="13u80lmd6y0q5" />
        <ClassicScenarios data-id="element-1533" data-mp="13x3wl55hafby" />
        <ClassicExpertise data-id="element-1534" data-mp="2jbro3g0jx93o" />
        <ClassicResearch data-id="element-1535" data-mp="2jazxykpq43ks" />
        <ClassicProcess data-id="element-1536" data-mp="2ja6r7jqrle2n" />
        <ClassicShortlist data-id="element-1537" data-mp="2jdm7gpw6ct5n" />
        <ClassicCases data-id="element-1538" data-mp="2fapj0ijye2ts" />
        <ClassicFounder data-id="element-1539" data-mp="2f94rl1sxr005" />
        <ClassicProof data-id="element-1540" data-mp="2f9wn6cmj8xqf" />
        <ClassicGeography data-id="element-1541" data-mp="2fciv4ddc5f96" />
        <ClassicGuaranteePrice data-id="element-1542" data-mp="3akutv8jflr9m" />
        <FaqSection items={c.faq.items} eyebrow={lang === 'EN' ? 'Questions & Answers' : 'Питання та відповіді'} title={c.faq.title} data-id="element-1543" data-mp="3alxfp21wd3io" />
        <ClassicDiscuss data-id="element-1544" data-mp="3jkrixb9qgs57" />
      </main>
      <Footer data-id="element-1545" data-mp="3jjpy4f2x0raa" />
    </div>;
}